'use client'

import { useEffect, useRef } from 'react'

import type { LogoBarProps } from './types'

/* Marquee speed in pixels per second. The animation always travels exactly one
   group width per cycle, so a fixed duration would make the bar scroll faster
   the more logos it holds. Deriving the duration from the measured group width
   (duration = distance / speed) keeps the perceived velocity the same for any
   amount of content and at any viewport width. */
const SPEED_PX_PER_SECOND = 40

/* Both halves of the track must carry identical layout classes: the flex
   algorithm only pins each to exactly half the track (so translateX(-50%) seams
   invisibly) while their flex-basis and grow match. Declared once so the two
   cannot drift apart. `flex-[1_0_50%]` + justify-content on the GROUP — never on
   the track — is the load-bearing part; see project-rules.md § "Duplicated-track
   CSS marquee". Under reduced motion the row wraps instead of overflowing, so
   every logo stays reachable once the scroll is gone. */
const GROUP_CLASSES =
	'flex flex-[1_0_50%] items-center justify-between gap-16 min-w-fit motion-reduce:flex-auto motion-reduce:flex-wrap motion-reduce:justify-center motion-reduce:gap-y-8 motion-reduce:min-w-0'

export default function LogoBar({ heading, logos }: LogoBarProps) {
	const trackRef = useRef<HTMLDivElement>(null)
	const groupRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		const track = trackRef.current
		const group = groupRef.current
		if (!track || !group) return

		const syncDuration = () => {
			const distance = group.getBoundingClientRect().width
			if (!distance) return
			track.style.setProperty('--logo-bar-duration', `${distance / SPEED_PX_PER_SECOND}s`)
		}

		syncDuration()

		/* Observing the group covers both content changes and viewport resizes,
		   and re-fires once the logo images have loaded and settled their width. */
		const observer = new ResizeObserver(syncDuration)
		observer.observe(group)
		return () => observer.disconnect()
	}, [logos.length])

	return (
		<section className="bg-black-950 pt-6 pb-16">
			<div className="container">
				{heading && (
					<p className="text-lg leading-normal font-semibold text-pure-white">{heading}</p>
				)}
				{/* Full-bleed below 2xl only — at 2xl and up the marquee stays inside the
				    .container box. Reduced motion drops the breakout too, otherwise a
				    static row would sit outside the page's own gutters. */}
				<div className="full-bleed mt-8 overflow-hidden 2xl:w-full 2xl:mx-0 motion-reduce:w-full motion-reduce:mx-0">
					<div
						ref={trackRef}
						className="flex w-[200%] min-w-fit items-center animate-[logo-bar-scroll_var(--logo-bar-duration,40s)_linear_infinite] motion-reduce:w-full motion-reduce:min-w-0 motion-reduce:animate-none"
					>
						<div ref={groupRef} className={GROUP_CLASSES}>
							{logos.map((logo, i) => (
								<figure key={`a-${i}`} className="flex shrink-0 items-center">
									<img src={logo.src} alt={logo.alt} className="h-15 w-auto max-w-40 object-contain" />
								</figure>
							))}
							{/* Zero-width trailing item: `justify-between` would otherwise pin the
							    last logo flush to the group's edge and swallow the spacing at the
							    seam between the two groups. Nothing to space once the loop is gone. */}
							<span className="w-0 shrink-0 motion-reduce:hidden" aria-hidden="true" />
						</div>
						{/* Clone exists only to seam the loop — pure duplication when static. */}
						<div className={`${GROUP_CLASSES} motion-reduce:hidden`} aria-hidden="true">
							{logos.map((logo, i) => (
								<figure key={`b-${i}`} className="flex shrink-0 items-center">
									<img src={logo.src} alt={logo.alt} className="h-15 w-auto max-w-40 object-contain" />
								</figure>
							))}
							<span className="w-0 shrink-0" aria-hidden="true" />
						</div>
					</div>
				</div>
			</div>
		</section>
	)
}
